import { z } from 'zod';
import { FOCOS, HORIZONTES } from './setup';

/* ─────────────────────────────────────────────────────────────────────
   Materiais e leituras do ADS.
   Os documentos do setup são lidos por um agente: o texto é extraído,
   dividido em trechos pesquisáveis e catalogado (tipo, resumo, fatos).
   Esse acervo é a base de consulta de todos os agentes — o objetivo,
   o Strategy e o Studio leem os mesmos materiais.
   ───────────────────────────────────────────────────────────────────── */

const t = (max: number) => z.string().trim().max(max);

/** O tipo de material, como aparece na etiqueta do arquivo. */
export const MATERIAL_CATEGORIES = [
  'Marca', 'Institucional', 'Estratégia', 'Comercial', 'Oferta', 'Pesquisa',
  'Financeiro', 'Pessoas', 'Operação', 'Outro',
] as const;
export type MaterialCategory = (typeof MATERIAL_CATEGORIES)[number];

/** O que o agente de leitura devolve para cada arquivo. */
export const materialReadSchema = z.object({
  title: t(140).min(1),
  category: z.enum(MATERIAL_CATEGORIES),
  summary: t(700).min(1),
  facts: z.array(t(280).min(1)).max(10).default([]),
  topics: z.array(t(48).min(1)).max(8).default([]),
});
export type MaterialRead = z.infer<typeof materialReadSchema>;

/** O catálogo guardado: a leitura do agente + o que a extração mediu. */
export interface MaterialCatalog extends MaterialRead {
  /** páginas, slides ou planilhas; null quando não se aplica */
  pages: number | null;
  /** como contar as "páginas" deste formato */
  unit: 'páginas' | 'slides' | 'planilhas' | 'arquivos' | null;
  words: number;
  /** documento grande: o agente leu o começo; a busca cobre o texto inteiro */
  partial: boolean;
  /** leitura sem IA (modo demonstração ou o modelo não respondeu) */
  basic: boolean;
}

export type MaterialReading = 'pending' | 'reading' | 'read' | 'failed' | 'skipped';

export interface MaterialDto {
  id: string;
  kind: 'document' | 'logo';
  /** setup: materiais do Início · evidence: documentos de acompanhamento (ex.: um indicador do Mapa de Gaps) */
  purpose: 'setup' | 'evidence';
  /** a que a evidência se liga: "gaps:<id do indicador>" */
  ref: string | null;
  filename: string;
  contentType: string;
  size: number;
  createdAt: string;
  reading: MaterialReading;
  catalog: MaterialCatalog | null;
  /** por que não foi lido (formato antigo, PDF só com imagem, erro do modelo…) */
  readNote: string | null;
  readAt: string | null;
}

/* ── leituras: cartões curtos, como o ADS comenta o que viu ───────── */

/** força: algo que já está bom · contexto: o que o conjunto mostra · lacuna: o que falta · proximo: o que vem a seguir */
export const INSIGHT_KINDS = ['forca', 'contexto', 'lacuna', 'proximo'] as const;
export const insightSchema = z.object({
  kind: z.enum(INSIGHT_KINDS),
  text: t(220).min(4),
});
export type Insight = z.infer<typeof insightSchema>;

export const materialsInsightsSchema = z.object({
  insights: z.array(insightSchema).min(1).max(4),
});

export interface MaterialsDto {
  items: MaterialDto[];
  /** leituras do conjunto; null enquanto não há material lido */
  insights: Insight[] | null;
  /** ainda há arquivo na fila de leitura */
  busy: boolean;
  basic: boolean;
}

/* ── objetivo: interpretação e força ──────────────────────────────── */

const score = z.number().int().min(0).max(100);

export const OBJECTIVE_CRITERIA = [
  { key: 'clareza',   label: 'Clareza do desafio',     weight: .30 },
  { key: 'aderencia', label: 'Aderência ao ADS',       weight: .25 },
  { key: 'impacto',   label: 'Potencial de impacto',   weight: .25 },
  { key: 'prontidao', label: 'Prontidão para avançar', weight: .20 },
] as const;
export type ObjectiveCriterion = (typeof OBJECTIVE_CRITERIA)[number]['key'];

export const objectiveAnalysisSchema = z.object({
  interpreted: t(420).min(10),
  horizon: z.enum(HORIZONTES),
  focus: z.enum(FOCOS),
  focus2: z.enum(FOCOS),
  scores: z.object({ clareza: score, aderencia: score, impacto: score, prontidao: score }),
  insights: z.array(insightSchema).min(1).max(4),
}).refine(a => a.focus !== a.focus2, { message: 'o foco secundário precisa ser diferente do principal', path: ['focus2'] });
export type ObjectiveAnalysis = z.infer<typeof objectiveAnalysisSchema>;

export function objectiveStrength(s: ObjectiveAnalysis['scores']): number {
  return Math.round(OBJECTIVE_CRITERIA.reduce((acc, c) => acc + s[c.key] * c.weight, 0));
}

export interface ObjectiveAnalysisDto {
  /** a frase que foi interpretada — se a frase mudou, a leitura está desatualizada */
  goal: string;
  analysis: ObjectiveAnalysis;
  strength: number;
  basic: boolean;
  /** quando a leitura é básica porque o modelo falhou, o porquê */
  note: string | null;
  updatedAt: string;
}

export const analyzeObjectiveSchema = z.object({
  goal: z.string().trim().min(20, 'escreva um pouco mais sobre o objetivo').max(500),
});

/** Normaliza para comparar frases: sem caixa, sem acento, sem espaço duplo. */
export const normGoal = (s: string) =>
  s.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase().replace(/\s+/g, ' ').trim();

/* ── Board: a leitura das entrevistas com a liderança ─────────────── */

export const boardReadingSchema = z.object({
  /** o que a liderança, junta, diz sobre a organização */
  sintese: t(700).min(10),
  /** as prioridades para os próximos 12 meses, consolidadas */
  prioridades: z.array(t(180).min(2)).min(1).max(5),
  /** onde as falas se encontram */
  convergencias: z.array(t(240).min(2)).max(4).default([]),
  /** onde divergem — o que o Strategy precisa resolver */
  divergencias: z.array(t(240).min(2)).max(4).default([]),
  insights: z.array(insightSchema).min(1).max(4),
});
export type BoardReading = z.infer<typeof boardReadingSchema>;

export interface BoardReadingDto {
  reading: BoardReading;
  /** quantas entrevistas e respostas entraram na leitura */
  interviews: number;
  answers: number;
  /** as respostas mudaram depois desta leitura */
  stale: boolean;
  basic: boolean;
  note: string | null;
  updatedAt: string;
}
